import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { TypedFormBuilder, TypedFormGroup } from '@gmrappdev/formsafe';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  share,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  PilotHttpClient,
  PilotQuery,
} from 'src/app/shared/flight-log-api/pilots/pilot-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { sleep } from 'src/util/async';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { parseBoolean, parseIntOrDefault } from 'src/util/values';
import { AirCarrierViewModel } from 'src/view-models/air-carrier-view-model';
import { PilotViewModel } from 'src/view-models/pilot-view-model';
import { PilotUpdateSubEntitiesStateDialogComponent } from '../pilot-update-sub-entities-state-dialog/pilot-update-sub-entities-state-dialog.component';

function trueOrUndefined(value: boolean) {
  return value === true ? true : undefined;
}
function stringOrUndefined(value: string | undefined) {
  return value == null || value === '' ? undefined : value;
}

type PilotQueryForm = Omit<PilotQuery, 'skip' | 'take'>;
interface RouteParams extends PilotQueryForm {
  page?: string;
  pageSize?: string;
}

@Component({
  selector: 'fl-pilot-list-page',
  templateUrl: './pilot-list-page.component.html',
  styleUrls: ['./pilot-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotListPageComponent implements OnInit, OnDestroy {
  public pilots$: Observable<PilotViewModel[]>;
  public total$: Observable<number>;

  public displayedColumns = [
    'firstName',
    'middleName',
    'lastName',
    'certificateNumber',
    'processingState',
    'lastProcessedDate',
    'processingError',
    'actions',
  ];

  public page$ = new Subject<PageEvent>();
  public defaultPageSize = 15;
  public pageSizeOptions = [this.defaultPageSize, 25, 50, 100];
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;

  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject<void>();

  public loadingKey = 'pilots-table';

  public pilotParameters: TypedFormGroup<PilotQueryForm>;

  public airCarrier$: Observable<AirCarrierViewModel>;

  constructor(
    private _pilot: PilotHttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: FlLoadingService,
    private _dialogService: TdDialogService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    public _tfb: TypedFormBuilder,
  ) {
    this.airCarrier$ = this._route.data.pipe(map((data) => data.airCarrier));

    const pilotsResults$ = combineLatest([
      this.airCarrier$,
      _route.queryParams,
      this._pageRefresh$,
    ]).pipe(
      switchMap(([airCarrier, params]) => {
        const pilotParams = this.parseRouteParams(params);
        this.markAsLoading();
        return this._pilot.getByAirCarrierId({
          airCarrierId: airCarrier.id,
          skip: pilotParams.skip,
          take: pilotParams.take,
          searchTerm: pilotParams.searchTerm,
          includeHiddenOnly: pilotParams.includeHiddenOnly,
        });
      }),
      distinctUntilChanged(),
      tap(() => this.markAsLoaded()),
      share(),
    );
    this.pilots$ = pilotsResults$.pipe(
      map((r) => r.results.sort((a, b) => a.lastName.localeCompare(b.lastName))),
    );
    this.total$ = pilotsResults$.pipe(map((r) => r.totalCount));
  }

  public ngOnInit() {
    this.pilotParameters = this._tfb.group<PilotQueryForm>({
      searchTerm: [undefined],
      includeHiddenOnly: [undefined],
    });

    this.pilotParameters.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        this.patchQueryParams(this.normalizePilotQueryFormParams(value));
      });

    this._route.queryParams.pipe(take(1)).subscribe((params: RouteParams) => {
      this.pilotParameters.patchValue(this.normalizePilotQueryFormParams(params), {
        emitEvent: false,
      });
      this.patchQueryParams(params);
    });

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });
  }

  public onPage(page: PageEvent) {
    this.page$.next(page);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  private normalizePilotQueryFormParams(params: RouteParams): PilotQueryForm {
    return {
      searchTerm: stringOrUndefined(params.searchTerm),
      includeHiddenOnly: trueOrUndefined(parseBoolean(params.includeHiddenOnly)),
    };
  }

  private parseRouteParams(queryParams: RouteParams): PilotQuery {
    const pageSize = parseIntOrDefault(queryParams.pageSize, this.defaultPageSize);
    return {
      ...this.normalizePilotQueryFormParams(queryParams),
      ...tryGetSkipTakeOrDefault(queryParams, pageSize),
    };
  }

  public async delete(pilot: PilotViewModel) {
    const result = await this._dialogService
      .openConfirm({
        title: 'Delete Pilot?',
        message: 'Are you sure you wish to delete this pilot?',
        acceptButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();
    if (result === true) {
      try {
        await this._pilot.delete(pilot.id).toPromise();
        this.markAsLoading();
        await sleep(2000);
        this._snackBar.open('Succesfully deleted pilot.', undefined, {
          duration: 2000,
        });
        this._pageRefresh$.next({});
      } catch (err) {
        this._snackBar.open('Error deleting pilot.', undefined, {
          duration: 2000,
        });

        throw err;
      }
    }
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate([`/prd/${airCarrierAbbreviation}/pilots`], { queryParams });
  }

  public refresh() {
    this._pageRefresh$.next({});
  }

  public bulkUpdate(pilotId: string) {
    this._dialog.open(PilotUpdateSubEntitiesStateDialogComponent, {
      data: {
        pilotId,
      },
      disableClose: true,
    });
  }
}
